import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { ConvexProvider, ConvexReactClient } from "convex/react";
import App from "./App";
import { convexApi } from "./convexApi";
import "./styles.css";
import "./styles/redesign.css";

const convexUrl = import.meta.env.VITE_CONVEX_URL as string | undefined;
const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;

if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((registration) => void registration.unregister());
    }).catch(() => undefined);
    if ("caches" in window) {
      caches.keys().then((keys) => {
        keys.filter((key) => key.startsWith("lexmosaic-pwa")).forEach((key) => void caches.delete(key));
      }).catch(() => undefined);
    }
  });
}

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

function AppTree({ dataMode }: { dataMode: "convex" | "demo" }) {
  return (
    <React.StrictMode>
      <AppErrorBoundary>
        <BrowserRouter>
          <App dataMode={dataMode} />
        </BrowserRouter>
      </AppErrorBoundary>
    </React.StrictMode>
  );
}

class AppErrorBoundary extends React.Component<{ children: React.ReactNode }, { error: string | null }> {
  state = { error: null };

  static getDerivedStateFromError(error: unknown) {
    return { error: getErrorMessage(error) };
  }

  componentDidCatch(error: unknown) {
    console.error("LexMosaic application error", error);
  }

  render() {
    if (this.state.error) {
      return (
        <StatusScreen
          title="LexMosaic could not open"
          body={this.state.error}
          actionLabel="Reload"
          onAction={() => window.location.reload()}
        />
      );
    }

    return this.props.children;
  }
}

function StatusScreen({ title, body, actionLabel, onAction }: { title: string; body?: string; actionLabel?: string; onAction?: () => void }) {
  return (
    <div className="grid min-h-screen place-items-center bg-app px-4 text-ink">
      <main className="w-full max-w-md rounded-xl border border-line bg-surface p-6 shadow-soft">
        <div className="mb-3 grid h-11 w-11 place-items-center rounded-lg bg-primary font-semibold text-white">L</div>
        <h1 className="text-2xl font-semibold">{title}</h1>
        {body ? <p className="mt-2 text-sm leading-6 text-secondary">{body}</p> : null}
        {actionLabel && onAction ? (
          <button type="button" className="mt-4 min-h-11 rounded-lg bg-primary px-4 text-sm font-semibold text-white" onClick={onAction}>
            {actionLabel}
          </button>
        ) : null}
      </main>
    </div>
  );
}

const APP_HOME_URL = `${window.location.origin}/`;

type AuthMode = "sign-in" | "sign-up";

/**
 * Read the requested auth mode before the address bar is cleaned, so the public
 * site can deep-link straight to observer registration with `?mode=sign-up`.
 */
function readRequestedAuthMode(): AuthMode {
  const search = new URLSearchParams(window.location.search);
  const hash = window.location.hash;
  const hashQuery = hash.includes("?") ? new URLSearchParams(hash.slice(hash.indexOf("?") + 1)) : null;
  return search.get("mode") === "sign-up" || hashQuery?.get("mode") === "sign-up" ? "sign-up" : "sign-in";
}

/**
 * Earlier sign-in round-trips nested `redirect_url` parameters into the URL
 * (app -> Clerk -> app -> ...), producing unusable addresses. Drop those
 * artifacts while leaving Clerk's own `__clerk*` handshake parameters intact.
 */
function cleanAuthArtifactsFromAddressBar() {
  try {
    const url = new URL(window.location.href);
    let changed = false;
    for (const key of ["redirect_url", "mode"]) {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    }
    if (url.hash && url.hash.includes("redirect_url")) {
      url.hash = "";
      changed = true;
    }
    if (changed) window.history.replaceState(null, "", url.toString());
  } catch {
    // A malformed URL should never block sign-in.
  }
}

/**
 * Embedded sign-in / registration. Mounting Clerk's components in the page
 * avoids the hosted-page redirect entirely, which is what previously looped
 * users back to this screen with ever-growing redirect_url parameters.
 * Registration is open: any new account enters the network as an Observer
 * (auto-provisioned by `bootstrap:provisionObserverAccount` after sign-in).
 */
function AuthScreen({ initialMode }: { initialMode: AuthMode }) {
  const [mode, setMode] = React.useState<AuthMode>(initialMode);
  const [mountFailed, setMountFailed] = React.useState(false);
  const mountRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const node = mountRef.current;
    const clerk = window.Clerk;
    if (!node || !clerk) return;
    const redirectOptions = {
      fallbackRedirectUrl: APP_HOME_URL,
    };
    try {
      if (mode === "sign-in") clerk.mountSignIn(node, redirectOptions);
      else if (clerk.mountSignUp) clerk.mountSignUp(node, redirectOptions);
      else throw new Error("Sign-up component unavailable.");
      setMountFailed(false);
    } catch (error) {
      console.error("LexMosaic could not mount the Clerk component", error);
      setMountFailed(true);
    }
    return () => {
      try {
        if (mode === "sign-in") clerk.unmountSignIn?.(node);
        else clerk.unmountSignUp?.(node);
      } catch {
        // Unmount failures are harmless; the node is replaced on next mount.
      }
    };
  }, [mode]);

  const openHostedSignIn = () => {
    void window.Clerk?.redirectToSignIn?.({
      fallbackRedirectUrl: APP_HOME_URL,
    });
  };

  const tabClass = (active: boolean) =>
    `min-h-11 rounded-lg px-4 text-sm font-semibold ${active ? "bg-primary text-white" : "bg-surface text-secondary"}`;

  return (
    <div className="grid min-h-screen place-items-center bg-app px-4 py-8 text-ink">
      <main className="w-full max-w-md">
        <div className="mb-4 flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-lg bg-primary font-semibold text-white">L</div>
          <div>
            <h1 className="text-2xl font-semibold">LexMosaic</h1>
            <p className="text-sm text-secondary">The private network for independent lawyers.</p>
          </div>
        </div>
        <div className="mb-3 grid grid-cols-2 gap-2 rounded-xl border border-line bg-surface p-1" role="tablist" aria-label="Sign in or create an account">
          <button type="button" role="tab" aria-selected={mode === "sign-in"} className={tabClass(mode === "sign-in")} onClick={() => setMode("sign-in")}>
            Sign in
          </button>
          <button type="button" role="tab" aria-selected={mode === "sign-up"} className={tabClass(mode === "sign-up")} onClick={() => setMode("sign-up")}>
            Create account
          </button>
        </div>
        <p className="mb-4 text-sm leading-6 text-secondary">
          {mode === "sign-up"
            ? "Registration is open. New accounts enter immediately as Observers: you can browse the network right away, and full membership is granted later by member vote."
            : "Sign in with the email and password (or provider) you registered with. No account yet? Create one — you enter immediately as an Observer."}
        </p>
        <div ref={mountRef} className="flex justify-center" />
        {mountFailed ? (
          <div className="rounded-xl border border-line bg-surface p-5">
            <p className="text-sm leading-6 text-secondary">The embedded form could not load in this browser.</p>
            <button type="button" className="mt-3 min-h-11 rounded-lg bg-primary px-4 text-sm font-semibold text-white" onClick={openHostedSignIn}>
              Open hosted sign in
            </button>
          </div>
        ) : null}
      </main>
    </div>
  );
}

function getErrorMessage(error: unknown) {
  const message = error instanceof Error ? error.message : typeof error === "string" ? error : "";
  if (message.includes("bootstrap:getAppData")) {
    const requestId = message.match(/Request ID:\s*([^\]\s]+)/)?.[1];
    return [
      "The private workspace could not load from Convex.",
      "Most often this means the signed-in Clerk email is not linked to an active LexMosaic member account, or the Convex backend functions need to be synced.",
      requestId ? `Convex request ID: ${requestId}` : null,
    ].filter(Boolean).join(" ");
  }
  if (message) return message;
  return "Unexpected startup error.";
}

let authScreenActive = false;

function showFatalError(error: unknown) {
  // While the sign-in / registration screen is up, background rejections from the
  // auth widget must not replace it and lock the user out; Clerk shows its own
  // field-level errors. A page reload after successful sign-in resets this flag.
  if (authScreenActive) {
    console.error("LexMosaic background error during sign-in", error);
    return;
  }
  root.render(
    <StatusScreen
      title="LexMosaic could not open"
      body={getErrorMessage(error)}
      actionLabel="Reload"
      onAction={() => window.location.reload()}
    />,
  );
}

window.addEventListener("error", (event) => {
  showFatalError(event.error ?? event.message);
});

window.addEventListener("unhandledrejection", (event) => {
  showFatalError(event.reason);
});

function loadClerkScript(publishableKey: string) {
  return new Promise<void>((resolve, reject) => {
    if (window.Clerk) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.async = true;
    script.crossOrigin = "anonymous";
    script.dataset.clerkPublishableKey = publishableKey;
    // Match the Clerk production instance major version while keeping the CDN dependency pinned.
    script.src = "https://cdn.jsdelivr.net/npm/@clerk/clerk-js@6/dist/clerk.browser.js";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Unable to load Clerk sign-in."));
    document.head.appendChild(script);
  });
}

async function waitForConvexToken() {
  for (let attempt = 0; attempt < 20; attempt += 1) {
    const token = await window.Clerk?.session?.getToken({ template: "convex", skipCache: attempt > 0 });
    if (token) return token;
    await new Promise((resolve) => window.setTimeout(resolve, 250));
  }
  return null;
}

async function renderLiveApp(convexDeploymentUrl: string, publishableKey: string) {
  root.render(<StatusScreen title="Opening LexMosaic..." />);
  const requestedAuthMode = readRequestedAuthMode();
  cleanAuthArtifactsFromAddressBar();
  await loadClerkScript(publishableKey);
  await window.Clerk?.load();

  if (!window.Clerk?.user) {
    authScreenActive = true;
    root.render(<AuthScreen initialMode={requestedAuthMode} />);
    return;
  }

  const token = await waitForConvexToken();
  if (!token) {
    root.render(<StatusScreen title="LexMosaic could not open" body="Your Clerk session is active, but the Convex JWT template named 'convex' did not return a token." />);
    return;
  }

  const convex = new ConvexReactClient(convexDeploymentUrl);
  convex.setAuth(async ({ forceRefreshToken }: { forceRefreshToken: boolean }) => {
    return (await window.Clerk?.session?.getToken({ template: "convex", skipCache: forceRefreshToken })) ?? token;
  });
  // Auto-provision an Observer account for first-time sign-ins (no-op if one exists).
  try {
    await convex.mutation(convexApi.bootstrap.provisionObserverAccount, {});
  } catch {
    // Non-fatal: the access screen will still guide an unprovisioned user.
  }
  root.render(
    <ConvexProvider client={convex}>
      <AppTree dataMode="convex" />
    </ConvexProvider>,
  );
}

const missingLiveConfig = [
  convexUrl ? null : "VITE_CONVEX_URL",
  clerkPublishableKey && !clerkPublishableKey.includes("YOUR_CLERK_KEY") ? null : "VITE_CLERK_PUBLISHABLE_KEY",
].filter(Boolean);

if (convexUrl && clerkPublishableKey && !clerkPublishableKey.includes("YOUR_CLERK_KEY")) {
  renderLiveApp(convexUrl, clerkPublishableKey).catch((error) => {
    showFatalError(error);
  });
} else if (import.meta.env.PROD) {
  root.render(<StatusScreen title="Live configuration missing" body={`The production app is missing: ${missingLiveConfig.join(", ")}.`} />);
} else {
  root.render(<AppTree dataMode="demo" />);
}
